/**
 * Conversations functionality for archive/card pages.
 * Handles the reply form under cards that can start a private conversation.
 */

const REPLY_BUTTON_SELECTOR = '.js-conversation-reply, .js-listing-reply';
const REPLY_FORM_SELECTOR = '.js-conversation-reply-form, .js-listing-reply-form';

const conversationCache = {};
let pendingListingDeleteUrl = null;
const conversationsI18n = (typeof window !== 'undefined' && window.novioI18n && window.novioI18n.conversations)
  ? window.novioI18n.conversations
  : {};

function conversationsT(key, replacements = {}) {
  let value = typeof conversationsI18n[key] === 'string' ? conversationsI18n[key] : key;

  Object.keys(replacements).forEach((replacementKey) => {
    const token = `{${replacementKey}}`;
    value = value.split(token).join(replacements[replacementKey]);
  });

  return value;
}

function getAjaxUrl() {
  if (typeof window !== 'undefined' && typeof window.linku_ajax_url !== 'undefined') {
    return window.linku_ajax_url;
  }

  return '/wp/wp-admin/admin-ajax.php';
}

function getParentData($element) {
  let parentPostId = parseInt($element.data('parent_post_id'), 10);
  if (!parentPostId) {
    parentPostId = parseInt($element.data('parent-post-id'), 10);
  }
  if (!parentPostId) {
    parentPostId = parseInt($element.data('listing_id'), 10);
  }
  if (!parentPostId) {
    parentPostId = parseInt($element.data('listing-id'), 10);
  }
  if (!parentPostId) {
    parentPostId = 0;
  }

  let parentPostType = $element.data('parent_post_type');
  if (!parentPostType) {
    parentPostType = $element.data('parent-post-type');
  }
  if (!parentPostType && parentPostId > 0) {
    parentPostType = 'listing';
  }

  let parentLabel = $element.data('parent_label');
  if (!parentLabel) {
    parentLabel = $element.data('parent-label');
  }
  if (!parentLabel) {
    parentLabel = 'item';
  }

  return {
    id: parentPostId,
    type: parentPostType,
    label: parentLabel,
  };
}

function getCacheKey(parent) {
  return `${parent.type || 'unknown'}:${parent.id || 0}`;
}

function getConversationsUrl(conversationId) {
  const conversationsLink = $('a[href*="gesprekken"]').first().attr('href') || '/uw-gesprekken/';
  return conversationId ? `${conversationsLink}?conversation=${conversationId}` : conversationsLink;
}

function formAllowsAnonymousReply($form) {
  const rawValue = $form.data('allow-anonymous-reply');
  return rawValue === true || rawValue === 1 || rawValue === '1';
}

function updateReplyIdentityPreview($form) {
  const $nameValue = $form.find('.js-conversation-reply-user-name-value').first();
  const $avatarImage = $form.find('.js-conversation-reply-avatar-img').first();
  const $avatarPlaceholder = $form.find('.js-conversation-reply-avatar-placeholder').first();
  const $anonymousCheckbox = $form.find('.js-conversation-anonymous-checkbox').first();

  const defaultNameRaw = $form.data('reply-name-default');
  const anonymousNameRaw = $form.data('reply-name-anonymous');
  const defaultName = typeof defaultNameRaw === 'string' ? defaultNameRaw : '';
  const anonymousName = typeof anonymousNameRaw === 'string' ? anonymousNameRaw : conversationsT('anonymous_member');

  const useAnonymousIdentity = formAllowsAnonymousReply($form)
    && $anonymousCheckbox.length
    && $anonymousCheckbox.is(':checked');

  if ($nameValue.length) {
    $nameValue.text(useAnonymousIdentity ? anonymousName : defaultName);
  }

  if (useAnonymousIdentity) {
    if ($avatarImage.length) {
      $avatarImage.hide();
    }
    $avatarPlaceholder.show();
    return;
  }

  if ($avatarImage.length) {
    $avatarImage.show();
    $avatarPlaceholder.hide();
  } else {
    $avatarPlaceholder.show();
  }
}

function showReplyError($form, message) {
  const $error = $form.find('.js-conversation-reply-error, .js-listing-reply-error').first();
  $error.html(`<i class="fas fa-exclamation-circle"></i> <span>${message}</span>`).show();
}

function showReplySuccess($form, message) {
  const $error = $form.find('.js-conversation-reply-error, .js-listing-reply-error').first();
  $error
    .removeClass('c-listing-reply__error')
    .addClass('c-listing-reply__success')
    .html(`<i class="fal fa-check-circle"></i> ${message}`)
    .show();

  setTimeout(() => {
    $error.removeClass('c-listing-reply__success').addClass('c-listing-reply__error').hide();
  }, 3000);
}

function clearProfileIncompleteHint($button) {
  const $hint = $button.siblings('.js-profile-incomplete-hint');
  if ($hint.length) {
    $hint.remove();
  }
}

function setProfileIncompleteHint($button, profileUrl) {
  clearProfileIncompleteHint($button);

  const normalizedUrl = typeof profileUrl === 'string' && profileUrl.trim() ? profileUrl.trim() : '/profiel/';
  const safeUrl = normalizedUrl.replace(/"/g, '&quot;');
  const profileLink = `<a href="${safeUrl}">${conversationsT('profile_link_text')}</a>`;
  const hintHtml = conversationsT('complete_profile_hint', { profile_link: profileLink });

  $button.after(`<p class="js-profile-incomplete-hint c-item-card__profile-incomplete-hint">${hintHtml}</p>`);
}

function isListingConversationReply($button) {
  return $button.hasClass('js-listing-reply') && $button.hasClass('js-conversation-reply');
}

function updateReplyButton($button, response) {
  if (!response.logged_in) {
    return;
  }

  if (response.exists && response.conversation_id && response.can_view) {
    clearProfileIncompleteHint($button);
    const conversationsUrl = getConversationsUrl(response.conversation_id);
    $button.removeClass('c-button--disabled').text(conversationsT('view_conversation'));
    $button.attr('href', conversationsUrl);
    return;
  }

  if (
    response.profile_complete === false
    && !isListingConversationReply($button)
  ) {
    $button.addClass('c-button--disabled').text(conversationsT('reply'));
    $button.attr('href', '#');
    setProfileIncompleteHint($button, response.profile_url);
    return;
  }

  if (!response.owner_available || response.is_owner) {
    clearProfileIncompleteHint($button);
    $button.addClass('c-button--disabled').text(conversationsT('reply'));
    $button.attr('href', '#');
    return;
  }

  if (response.exists && !response.can_view) {
    clearProfileIncompleteHint($button);
    $button.addClass('c-button--disabled').text(conversationsT('conversation_ended'));
    $button.attr('href', '#');
    return;
  }

  clearProfileIncompleteHint($button);
  $button.removeClass('c-button--disabled').text(conversationsT('reply'));
  $button.attr('href', '#');
}

function processConversationResponse(response, $form, $button, parent) {
  if (!response.logged_in) {
    window.location.href = $button.attr('href') || window.location.href;
    return;
  }

  if (!response.owner_available) {
    showReplyError($form, conversationsT('owner_unavailable', { label: parent.label }));
    $form.slideDown(200);
    return;
  }

  if (response.is_owner) {
    showReplyError($form, conversationsT('cannot_reply_own', { label: parent.label }));
    $form.slideDown(200);
    return;
  }

  if (response.profile_complete === false && !response.exists) {
    if (!isListingConversationReply($button)) {
      const profileUrl = response.profile_url || '/profiel/';
      const safeUrl = profileUrl.replace(/"/g, '&quot;');
      showReplyError(
        $form,
        response.profile_message || conversationsT('complete_profile_hint', {
          profile_link: `<a href="${safeUrl}">${conversationsT('profile_link_text')}</a>`,
        }),
      );
      $form.slideDown(200);
      return;
    }

    const profileUrl = response.profile_url || '/profiel/';
    const safeUrl = profileUrl.replace(/"/g, '&quot;');
    showReplyError(
      $form,
      response.profile_message || conversationsT('complete_profile_hint', {
        profile_link: `<a href="${safeUrl}">${conversationsT('profile_link_text')}</a>`,
      }),
    );
    $form.find('.js-conversation-reply-message, .js-listing-reply-message')
      .attr('disabled', true);
    $form.find('.js-conversation-reply-send, .js-listing-reply-send')
      .addClass('c-button--disabled')
      .attr('disabled', true);
    $form.slideDown(200);
    return;
  }

  if (response.exists) {
    if (!response.can_view) {
      showReplyError(
        $form,
        conversationsT('conversation_already_ended_for_parent', { label: parent.label }),
      );
      $form.slideDown(200);
      return;
    }

    const conversationUrl = getConversationsUrl(response.conversation_id);
    const safeConversationUrl = conversationUrl.replace(/"/g, '&quot;');
    const conversationLink = `<a href="${safeConversationUrl}">${conversationsT('view_your_conversation')}</a>`;
    showReplyError(
      $form,
      conversationsT('already_replied_with_link', { label: parent.label, conversation_link: conversationLink }),
    );
    $form.slideDown(200);
    return;
  }

  $form.find('.js-conversation-reply-error, .js-listing-reply-error').hide().html('');
  $form.slideToggle(200);
}

function checkConversationStatus(parent) {
  return new Promise((resolve, reject) => {
    if (!parent.id || !parent.type) {
      reject(new Error('Missing parent id/type'));
      return;
    }

    const cacheKey = getCacheKey(parent);
    if (conversationCache[cacheKey]) {
      resolve(conversationCache[cacheKey]);
      return;
    }

    const payload = {
      action: 'check_existing_conversation',
      parent_post_id: parent.id,
      parent_post_type: parent.type,
    };

    if (parent.type === 'listing') {
      payload.listing_id = parent.id;
    }

    $.ajax({
      url: getAjaxUrl(),
      type: 'post',
      data: payload,
      dataType: 'json',
      success(response) {
        conversationCache[cacheKey] = response;
        resolve(response);
      },
      error(response) {
        console.error('Error checking conversation:', response);
        reject(response);
      },
    });
  });
}

function submitReply($form, parent) {
  const $textarea = $form.find('.js-conversation-reply-message, .js-listing-reply-message');
  const $sending = $form.find('.js-conversation-reply-sending, .js-listing-reply-sending');
  const $error = $form.find('.js-conversation-reply-error, .js-listing-reply-error');
  const $submitForm = $form.find('.js-conversation-reply-submit, .js-listing-reply-submit');
  const sendAnonymously = formAllowsAnonymousReply($form)
    && $form.find('.js-conversation-anonymous-checkbox').is(':checked');
  const message = $textarea.val().trim();

  if (!message) {
    showReplyError($form, conversationsT('empty_message'));
    return;
  }

  if (!parent.id || !parent.type) {
    showReplyError($form, conversationsT('invalid_link'));
    return;
  }

  $submitForm.hide();
  $sending.show();
  $error.hide();

  const payload = {
    action: 'start_conversation',
    parent_post_id: parent.id,
    parent_post_type: parent.type,
    message,
  };

  if (parent.type === 'listing') {
    payload.listing_id = parent.id;
  }

  if (formAllowsAnonymousReply($form)) {
    payload.is_anonymous = sendAnonymously ? '1' : '0';
  }

  $.ajax({
    url: getAjaxUrl(),
    type: 'post',
    data: payload,
    dataType: 'json',
    success(response) {
      if (response.success) {
        if (response.redirect_url) {
          window.location.href = response.redirect_url;
        } else {
          $textarea.val('');
          $form.find('.js-conversation-anonymous-checkbox').prop('checked', false);
          updateReplyIdentityPreview($form);
          $sending.hide();
          $submitForm.show();
          showReplySuccess($form, conversationsT('message_sent'));
        }
      } else {
        $sending.hide();
        $submitForm.show();
        showReplyError($form, response.message || conversationsT('generic_error'));
      }
    },
    error(response) {
      console.error('Error starting conversation:', response);
      $sending.hide();
      $submitForm.show();

      let errorMessage = conversationsT('generic_error');
      if (response.responseJSON && response.responseJSON.message) {
        errorMessage = response.responseJSON.message;
      }

      showReplyError($form, errorMessage);
    },
  });
}

function handleReplyButtonClick($button) {
  const parent = getParentData($button);
  const $wrapper = $button.closest('.c-item-card-wrapper');
  const $form = $wrapper.find(REPLY_FORM_SELECTOR).first();

  updateReplyIdentityPreview($form);

  $(REPLY_FORM_SELECTOR).not($form).slideUp(200);

  const cacheKey = getCacheKey(parent);
  const cachedResponse = conversationCache[cacheKey] || $button.data('conversation-status');

  if (cachedResponse) {
    updateReplyButton($button, cachedResponse);

    if (
      cachedResponse.exists
      && cachedResponse.can_view
      && cachedResponse.logged_in
      && !cachedResponse.is_owner
    ) {
      window.location.href = getConversationsUrl(cachedResponse.conversation_id);
      return;
    }

    processConversationResponse(cachedResponse, $form, $button, parent);
    return;
  }

  checkConversationStatus(parent)
    .then((response) => {
      updateReplyButton($button, response);

      if (response.exists && response.can_view && response.logged_in && !response.is_owner) {
        window.location.href = getConversationsUrl(response.conversation_id);
        return;
      }

      processConversationResponse(response, $form, $button, parent);
    })
    .catch(() => {
      $form.slideToggle(200);
    });
}

function applyConversationStatusToButton($button, response) {
  const $wrapper = $button.closest('.c-item-card-wrapper');
  const $form = $wrapper.length
    ? $wrapper.find(REPLY_FORM_SELECTOR).first()
    : $(REPLY_FORM_SELECTOR).first();

  $form.data('conversation-status', response);
  $button.data('conversation-status', response);
  updateReplyButton($button, response);
}

// One batched request for all cards instead of one request per card.
function checkAllParentConversations() {
  const $buttons = $(REPLY_BUTTON_SELECTOR);
  if ($buttons.length === 0) {
    return;
  }

  const buttonsByKey = {};
  const parentsToCheck = {};

  $buttons.each(function eachReplyButton() {
    const $button = $(this);
    const parent = getParentData($button);

    if (!parent.id || !parent.type) {
      return;
    }

    const cacheKey = getCacheKey(parent);
    if (!buttonsByKey[cacheKey]) {
      buttonsByKey[cacheKey] = [];
    }
    buttonsByKey[cacheKey].push($button);

    if (conversationCache[cacheKey]) {
      applyConversationStatusToButton($button, conversationCache[cacheKey]);
    } else {
      parentsToCheck[cacheKey] = { id: parent.id, type: parent.type };
    }
  });

  const parents = Object.values(parentsToCheck);
  if (parents.length === 0) {
    return;
  }

  $.ajax({
    url: getAjaxUrl(),
    type: 'post',
    data: {
      action: 'check_existing_conversations',
      parents,
    },
    dataType: 'json',
    success(response) {
      const results = (response && response.results) || {};
      Object.keys(results).forEach((cacheKey) => {
        const status = results[cacheKey];
        if (!status || !status.success || !buttonsByKey[cacheKey]) {
          return;
        }

        conversationCache[cacheKey] = status;
        buttonsByKey[cacheKey].forEach(($button) => {
          applyConversationStatusToButton($button, status);
        });
      });
    },
    error(response) {
      console.error('Error checking conversations:', response);
    },
  });
}

// Legacy entry point used from filters.js
function checkAllListingConversations() {
  checkAllParentConversations();
}

if (typeof window !== 'undefined') {
  window.checkAllListingConversations = checkAllListingConversations;
}

function openListingDeleteModal($modal, deleteUrl) {
  if ($modal.length === 0 || !deleteUrl) {
    return;
  }

  pendingListingDeleteUrl = deleteUrl;
  $modal.removeAttr('hidden').addClass('is-open');
}

function closeListingDeleteModal($modal) {
  if ($modal.length === 0) {
    return;
  }

  pendingListingDeleteUrl = null;
  $modal.removeClass('is-open').attr('hidden', 'hidden');
}

$(document).ready(() => {
  const $listingDeleteModal = $('.js-listing-delete-modal');

  $(document).on('click', REPLY_BUTTON_SELECTOR, function onReplyButtonClick(e) {
    const $button = $(this);
    const href = $button.attr('href');

    if (href && href !== '#') {
      return;
    }

    e.preventDefault();
    handleReplyButtonClick($button);
  });

  $(document).on('click', '.js-listing-reply-close', function onReplyCloseClick() {
    const $form = $(this).closest(REPLY_FORM_SELECTOR);
    $form.slideUp(200);
  });

  $(document).on('change', '.js-conversation-anonymous-checkbox', function onAnonymousToggleChange() {
    const $form = $(this).closest(REPLY_FORM_SELECTOR);
    updateReplyIdentityPreview($form);
  });

  $(document).on(
    'submit',
    '.js-conversation-reply-submit, .js-listing-reply-submit',
    function onReplySubmit(e) {
      e.preventDefault();
      const $form = $(this).closest(REPLY_FORM_SELECTOR);
      const parent = getParentData($form);
      submitReply($form, parent);
    },
  );

  $(document).on(
    'click',
    '.js-conversation-reply-send, .js-listing-reply-send',
    function onReplySendClick(e) {
      e.preventDefault();
      const $form = $(this).closest(REPLY_FORM_SELECTOR);
      const parent = getParentData($form);
      submitReply($form, parent);
    },
  );

  $(document).on('click', '.js-listing-delete-trigger', function onListingDeleteTriggerClick(e) {
    if ($listingDeleteModal.length === 0) {
      return;
    }

    e.preventDefault();
    const deleteUrl = $(this).data('delete-url') || $(this).attr('href');
    openListingDeleteModal($listingDeleteModal, deleteUrl);
  });

  $(document).on('click', '.js-listing-delete-cancel, .js-listing-delete-modal-close', (e) => {
    e.preventDefault();
    closeListingDeleteModal($listingDeleteModal);
  });

  $(document).on('click', '.js-listing-delete-confirm', (e) => {
    e.preventDefault();
    if (pendingListingDeleteUrl) {
      window.location.href = pendingListingDeleteUrl;
    }
  });

  $(document).on('keydown', (e) => {
    if (e.key === 'Escape' && $listingDeleteModal.hasClass('is-open')) {
      closeListingDeleteModal($listingDeleteModal);
    }
  });

  $(REPLY_FORM_SELECTOR).each(function initializeReplyPreview() {
    updateReplyIdentityPreview($(this));
  });
});
